import React, { useEffect, useRef } from 'react';
import { Vector as VectorLayer } from 'ol/layer';
import { Vector as VectorSource } from 'ol/source';
import { Feature, Map } from 'ol';
import { Circle, Fill, Stroke, Style, Text } from 'ol/style';
import { fromLonLat } from 'ol/proj';
import { Point } from 'ol/geom';
import { useCrewLocations } from '@/hooks/api/useCrewLocations';
import { FeatureLike } from 'ol/Feature';
import { useLayerById, useLayerByKey } from '@/stores/mapStore';

type AssetLayerProps = {
  map: Map;
};

const userStyle = (feature: FeatureLike) => {
  return new Style({
    image: new Circle({
      radius: 6,
      fill: new Fill({ color: '#3b82f6' }),
      stroke: new Stroke({ color: '#fff', width: 2 }),
    }),
    text: new Text({
      text: (feature.get('name') as string) || '',
      offsetY: -15,
      font: '12px sans-serif',
      fill: new Fill({ color: '#000' }),
      stroke: new Stroke({ color: '#fff', width: 2 }),
    }),
  });
};

export const UserTrackingLayer: React.FC<AssetLayerProps> = ({ map }) => {
  const layerRef = useRef<VectorLayer<VectorSource> | null>(null);
  const sourceRef = useRef<VectorSource | null>(null);
  // Get layer config from store
  const layerDef = useLayerByKey('CREW_LOCATION');

  const { locations, isError } = useCrewLocations();

  // ── Layer initialization ────────────────────────────────────────────────
  useEffect(() => {
    if (!map) return;

    const source = new VectorSource();
    const layer = new VectorLayer({
      source,
      style: userStyle,
      visible: true,
      zIndex: 10,
      properties: { config: layerDef },
    });

    sourceRef.current = source;
    layerRef.current = layer;
    map.addLayer(layer);

    return () => {
      map.removeLayer(layer);
      layerRef.current = null;
      sourceRef.current = null;
    };
  }, [map]);

  // ── Sync locations → OL source ──────────────────────────────────────────
  // useCrewLocations owns all socket wiring and query state.
  // This effect is purely a rendering concern: reconcile the locations
  // array into the OL VectorSource via upsert to avoid full redraws.
  useEffect(() => {
    const source = sourceRef.current;
    if (!source) return;

    const incoming = new Set(locations.map((loc) => loc.userId));

    // Remove locations if user goes offline
    source.getFeatures().forEach((f) => {
      if (!incoming.has(f.getId() as string)) {
        source.removeFeature(f);
      }
    });

    // Upsert each live location
    locations.forEach((loc: any) => {
      const coords = fromLonLat([loc.longitude, loc.latitude]);
      const existing = source.getFeatureById(loc.userId);

      if (existing) {
        (existing.getGeometry() as Point).setCoordinates(coords);
        existing.set('name', loc?.user?.name);
      } else {
        const feature = new Feature({
          geometry: new Point(coords),
          name: loc?.user?.name || '',
        });
        feature.setId(loc.userId);
        source.addFeature(feature);
      }
    });
  }, [locations]);

  if (isError) {
    console.error(`[UserTrackingLayer] Failed to load locations"`);
  }

  return null;
};
